import type { AdminMedia } from '@diora/contracts';
import { Upload } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdminMutation } from '@/hooks/admin';
import { apiUpload } from '@/lib/api';
import { qk } from '@/lib/query';
import { Button } from './ui';

const URL = '/admin/media';

/**
 * Upload one image.
 *
 * Extracted so the media page and the image picker cannot drift: the accepted
 * types, the "same file twice" reset and the hidden input's accessibility
 * treatment are each a decision, and three of them were already written down
 * once here.
 */
export function UploadButton({
  variant = 'primary',
  size,
  onUploaded,
}: {
  variant?: 'primary' | 'secondary';
  size?: 'sm';
  /** Called with the created asset — the picker uses it to choose what you just added. */
  onUploaded?: (media: AdminMedia) => void;
}) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = useAdminMutation(
    (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return apiUpload<AdminMedia>(URL, form);
    },
    {
      successMessage: t('media.uploaded'),
      invalidate: [['media'], qk.dashboard],
      onSuccess: (result) => onUploaded?.(result.data),
    },
  );

  return (
    <>
      {/* `sr-only` clips this input; it does not remove it, so it used to be the
          first tab stop on the media page — an invisible, unnamed 1×1 box in
          front of the button that already does the job. The button is the real
          control, so this leaves the tab order and the accessibility tree
          entirely. `.click()` still works. */}
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/avif"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) upload.mutate(file);
          // Clear it, or choosing the same file twice fires nothing.
          event.target.value = '';
        }}
      />
      <Button
        variant={variant}
        size={size}
        loading={upload.isPending}
        onClick={() => fileInput.current?.click()}
      >
        <Upload className="dio-icon" aria-hidden="true" />
        {upload.isPending ? t('media.uploading') : t('media.upload')}
      </Button>
    </>
  );
}
